/**
 * `useClipboardHandlers` — paste, copy, drag-over, drop handlers + plain-text
 * paste intent. Extracted from PlateEditor.tsx so the main shell stays focused
 * on render orchestration rather than clipboard policy.
 *
 * Returns the four event handlers ready to attach to `<PlateContent>` /
 * `<Plate>`, plus `markNextPasteAsPlain()` — call this from the editor's
 * keydown handler when ⌘⇧V / Ctrl⇧V is detected. The paste event fires
 * after keydown, so we record intent here and consume it in `handlePaste`.
 *
 * What each handler does:
 *
 *  handlePaste:
 *    - ⌘⇧V intent: insert clipboard's plain text only, splitting on
 *      newlines into multiple paragraphs. Ignores HTML AND Slate's
 *      `application/x-slate-fragment` back-channel (which would otherwise
 *      restore every mark from the source editor). Mirrors Google Docs /
 *      Notion behavior.
 *    - Image file in clipboard: routes through `handleImageInsert` (the
 *      shared upload+insert pipeline). Toast on invalid mime / too-large.
 *    - Genuine spreadsheet HTML (Excel / Google Sheets, per
 *      `isSpreadsheetTableHtml`): routes through `spreadsheetHtmlToPlateTable`
 *      for fidelity. Detection MUST run before `sanitizeOfficeHtml` strips the
 *      `<style>` blocks Excel uses as its marker. Document tables (Google Docs,
 *      generic HTML) deliberately fall through to the generic path below, which
 *      preserves in-cell lists the converter would flatten.
 *    - Everything else (the common case, incl. document tables): runs through
 *      `deserializePastedHtml` (sanitise → strip `data-slate-*` → flatten/enrich
 *      lists → deserialize → layout-tables → strip newlines) and inserts the
 *      resulting fragment. Plain-text fallback if it deserializes to nothing.
 *
 *  handleDrop:
 *    - File drop of any image MIME → routes through `handleImageInsert`.
 *    - preventDefault always fires to dodge Slate's "Cannot resolve a Slate
 *      range" error on file drops.
 *
 *  handleDragOver:
 *    - Calls preventDefault when the drag payload includes Files, so the
 *      browser will dispatch the drop event to us instead of refusing it.
 *
 *  handleCopy:
 *    - Clones the current selection, runs `cleanDomForExport` (strips
 *      editor-only UI + normalises tables / images / data-slate attrs),
 *      and writes the cleaned HTML to `text/html` + plain text to
 *      `text/plain`. So pasting from this editor into Google Docs / Word
 *      / a plain-text input all produce the right shape.
 *    - A multi-cell table selection writes its rectangle instead: the exact
 *      Plate AST to `x-slate-fragment` (with source table/row props), the
 *      rendered cell DOM assembled into a table for `text/html` (source
 *      formatting: cell backgrounds, marks, lists), and tab-separated cell
 *      text for `text/plain`.
 *
 *  handleCut:
 *    - Same clipboard write as handleCopy, then clears the selection's
 *      content: a multi-cell table rectangle has each cell reset directly
 *      (no single Slate Range spans it); anything else goes through the
 *      standard deleteFragment transform.
 */
import { useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { NodeApi } from 'platejs';
import { getTableAbove, getSelectedCells, getEmptyCellNode, getEmptyRowNode } from '@platejs/table';
import { isSpreadsheetTableHtml, isPureTableHtml, spreadsheetHtmlToPlateTable } from '../serializers/spreadsheetHtmlToPlate';
import {
    deserializePastedHtml,
    stripNewlinesFromFragment,
    enrichPastedListElements,
    reattachTableColSizes,
    tableFragmentColumnCount,
    encodeSlateFragment,
    decodeSlateFragment,
    unwrapSingleCellTables,
} from '../serializers/pasteNormalize';
import { simplifyPastedFragment } from '../serializers/simplifyPastedFragment';
import { handleImageInsert, rehostRemoteImagesAfterPaste } from '../components/ImagePlugin/handleImageInsert';
import { cleanDomForExport } from '../exporters/docxExport';
import { scrollCaretIntoView } from './scrollCaretIntoView';
import { sanitizePlateFragment } from '../serializers/sanitizePlateFragment';
import { getSelectedCell, clearCellSelection } from '../components/TableElements/cellSelection';

// @platejs/table v53.0.9's `withInsertFragmentTable` → `insertTableRow` spreads
// a pasted single-`table` fragment cell-by-cell into the table under the caret
// when the caret sits inside an existing cell. Its row-expansion bookkeeping
// mis-tracks the cell path once the source table has more rows than fit under
// the caret, and it eventually calls `replaceNodes` at a path that no longer
// exists ("Cannot find a descendant at path"). Pasting into an empty paragraph
// is unaffected — that's a different (correct) branch of the same function.
//
// Fix: detect the exact trigger (single-table fragment + caret inside a cell)
// and sidestep the buggy library path entirely by hand-rolling the spread —
// write the source grid cell-by-cell into the destination table starting at
// the caret's [row, col], growing the destination table (new rows/columns)
// as needed. The pasted cells bring their formatting (CELL_FORMAT_PROPS) and
// unsized/grown columns adopt the source widths — see the comments inside
// spreadTableIntoDestination. `getEmptyCellNode`/`getEmptyRowNode` are pure structural
// builders (they call the table plugin's own type-aware cell factory) — they
// do NOT route through `insertTableRow`, so using them here never re-enters
// the buggy path.
// The caret's selection path is `[...tablePath, row, col, ...restIntoTheCell]`
// — slicing it to the two indices right after the table path gives the
// destination cell's [row, col] directly, the same "read the path" approach
// `buildSelectedCellsTableFragment` uses via `editor.api.findPath`.
function destinationCellOrigin(editor: any, tablePath: number[]): [number, number] | null {
    const selectionPath: number[] | undefined = editor.selection?.anchor?.path;
    if (!Array.isArray(selectionPath) || selectionPath.length < tablePath.length + 2) return null;
    return [selectionPath[tablePath.length], selectionPath[tablePath.length + 1]];
}

// Cell-level FORMATTING props that travel with a pasted cell (Excel-like:
// fill/alignment come along with the content). Structural attrs (type td/th,
// colSpan/rowSpan) are deliberately NOT here — the paste writes into the
// destination grid, it doesn't restructure it.
const CELL_FORMAT_PROPS = ['background', 'backgroundColor', 'verticalAlign'] as const;

function spreadTableIntoDestination(editor: any, tablePath: number[], originRow: number, originCol: number, sourceTable: any): void {
    const sourceRows: any[] = Array.isArray(sourceTable?.children) ? sourceTable.children : [];
    const sourceCols = sourceRows.map((row) => row?.children?.length ?? 0);
    const sourceWidth = sourceCols.length ? Math.max(...sourceCols) : 0;
    if (!sourceRows.length || !sourceWidth) return;

    editor.tf.withoutNormalizing(() => {
        // Re-read the table fresh each time since row/col growth below mutates it.
        const readTable = (): any => editor.api.node(tablePath)?.[0];
        const readRows = (): any[] => readTable()?.children ?? [];

        const neededCols = originCol + sourceWidth;
        const neededRows = originRow + sourceRows.length;

        // 1. Grow existing rows to `neededCols` columns. Computed per row (not
        // gated by the table's widest row) so a ragged table — where some rows
        // are already wider than `neededCols` but THIS row isn't — still gets
        // its own missing cells filled instead of being silently left short.
        readRows().forEach((row: any, r: number) => {
            const rowWidth = row?.children?.length ?? 0;
            const missingForRow = neededCols - rowWidth;
            for (let i = 0; i < missingForRow; i++) {
                editor.tf.insertNodes(getEmptyCellNode(editor, {}), { at: [...tablePath, r, rowWidth + i] });
            }
        });
        // Column widths: an explicitly-sized destination column keeps its
        // width (Excel keeps destination widths on paste); columns grown for
        // this paste and never-sized (0) columns covered by it adopt the
        // source column's width — pasting a formatted table into a fresh
        // default table reproduces the source's look. A grown column with no
        // source width falls back to the last destination width.
        const tableNode = readTable();
        const destColSizes: number[] = Array.isArray(tableNode?.colSizes) ? tableNode.colSizes : [];
        const sourceColSizes: number[] = Array.isArray(sourceTable?.colSizes) ? sourceTable.colSizes : [];
        if (destColSizes.length > 0 || sourceColSizes.length > 0) {
            const widestRow = Math.max(neededCols, ...readRows().map((row: any) => row?.children?.length ?? 0));
            const lastDestWidth = destColSizes.length ? destColSizes[destColSizes.length - 1] : 0;
            const next = Array.from({ length: widestRow }, (_, i) => {
                const dest = destColSizes[i];
                const sourceIdx = i - originCol;
                const source = sourceIdx >= 0 && sourceIdx < sourceWidth ? sourceColSizes[sourceIdx] : undefined;
                if (typeof dest === 'number' && dest > 0) return dest;
                if (typeof source === 'number' && source > 0) return source;
                if (i >= destColSizes.length) return lastDestWidth;
                return typeof dest === 'number' ? dest : 0;
            });
            if (next.length !== destColSizes.length || next.some((w, i) => w !== destColSizes[i])) {
                editor.tf.setNodes({ colSizes: next }, { at: tablePath });
            }
        }

        // 2. Grow the table with new empty rows to `neededRows`.
        const rowCountAfterColGrowth = readRows().length;
        if (neededRows > rowCountAfterColGrowth) {
            const colCountNow = readRows()[0]?.children?.length ?? neededCols;
            for (let r = rowCountAfterColGrowth; r < neededRows; r++) {
                editor.tf.insertNodes(getEmptyRowNode(editor, { colCount: colCountNow }), { at: [...tablePath, r] });
            }
        }

        // 3. Overwrite each destination cell's children with the source cell's
        // children (deep-cloned so the pasted content doesn't share node
        // references with the clipboard fragment), and carry the source cell's
        // FORMATTING props with them (see CELL_FORMAT_PROPS) — a format prop
        // the source cell lacks is cleared off the destination, Excel-style.
        // The destination cell keeps its structural attrs (td vs th, colSpan).
        sourceRows.forEach((sourceRow: any, sr: number) => {
            (sourceRow?.children ?? []).forEach((sourceCell: any, sc: number) => {
                const destRow = originRow + sr;
                const destCol = originCol + sc;
                const cellPath = [...tablePath, destRow, destCol];
                const destCell = editor.api.node(cellPath)?.[0];
                if (!destCell) return;
                const clonedChildren = JSON.parse(JSON.stringify(sourceCell?.children ?? []));
                const existingChildCount = destCell.children?.length ?? 0;
                // Remove the cell's existing children (repeatedly removing index 0 —
                // removeNodes shifts later siblings down, so this drains the cell
                // without recomputing paths per iteration).
                for (let i = 0; i < existingChildCount; i++) {
                    editor.tf.removeNodes({ at: [...cellPath, 0] });
                }
                clonedChildren.forEach((child: any, i: number) => {
                    editor.tf.insertNodes(child, { at: [...cellPath, i] });
                });
                const formatProps: Record<string, unknown> = {};
                const staleKeys: string[] = [];
                for (const key of CELL_FORMAT_PROPS) {
                    if (sourceCell?.[key] !== undefined) formatProps[key] = sourceCell[key];
                    else if (destCell?.[key] !== undefined) staleKeys.push(key);
                }
                if (Object.keys(formatProps).length) editor.tf.setNodes(formatProps, { at: cellPath });
                if (staleKeys.length) editor.tf.unsetNodes(staleKeys, { at: cellPath });
            });
        });
    });
}

export function insertPastedFragment(editor: any, fragment: any[]): void {
    const isSingleTable = Array.isArray(fragment) && fragment.length === 1 && fragment[0]?.type === 'table';
    if (isSingleTable) {
        try {
            const tableEntry = getTableAbove(editor, { at: editor.selection?.anchor });
            if (tableEntry) {
                // A 1×1 source is CONTENT, not a grid: Excel/Sheets/Docs put a
                // single-cell copy on the clipboard as a one-cell table.
                // Spreading it would REPLACE the destination cell's children —
                // unwrap and insert at the caret like any text paste, so it
                // APPENDS. (In-editor copies are already unwrapped at copy time
                // by unwrapSingleCellTables; this guards external sources and
                // clipboards written before that fix.)
                const soleRow = fragment[0]?.children?.length === 1 ? fragment[0].children[0] : null;
                const soleCell = soleRow?.children?.length === 1 ? soleRow.children[0] : null;
                if (soleCell) {
                    const content = Array.isArray(soleCell.children) ? soleCell.children : [];
                    if (content.length) editor?.tf?.insertFragment?.(JSON.parse(JSON.stringify(content)));
                    return;
                }
                const [, tablePath] = tableEntry;
                const origin = destinationCellOrigin(editor, tablePath);
                if (origin) {
                    const [originRow, originCol] = origin;
                    spreadTableIntoDestination(editor, tablePath, originRow, originCol, fragment[0]);
                    return;
                }
                // Caret is inside the table but not resolvable to a cell (shouldn't
                // normally happen) — fall back to the previous sibling-insert
                // behavior rather than risk a malformed spread.
                const afterPath = [...tablePath.slice(0, -1), tablePath[tablePath.length - 1] + 1];
                editor?.tf?.insertNodes?.(fragment[0], { at: afterPath, select: true });
                return;
            }
        } catch {
            /* defensive — fall through to the default insertFragment path */
        }
    }
    editor?.tf?.insertFragment?.(fragment);
}

// A plain click range-selects the WHOLE cell (Excel-style single-cell select —
// see cellSelection.ts). Pasting over that full-cell range makes the
// insertFragment / insertText paths below DELETE the whole cell first and
// REPLACE its content, so the paste overwrites the cell instead of adding to it.
// Collapse the selection to the cell's END before inserting so a paste APPENDS
// to the existing content. Returns true only when the caret was actually moved
// to the cell's end; false when no cell is selected OR the selected-cell node
// can no longer be resolved to a caret target (a stale node after removal) —
// the selected-cell visual state is cleared either way, and the paste then
// proceeds against whatever Slate selection is genuinely live.
// (Type-to-overwrite intentionally still replaces — that path is the keydown
// handler, not paste.)
export function collapseSelectedCellForPaste(editor: any): boolean {
    const cell = getSelectedCell(editor);
    if (!cell) return false;
    let collapsed = false;
    try {
        const cellPath = editor?.api?.findPath?.(cell);
        const end = cellPath && editor?.api?.end?.(cellPath);
        if (end) {
            editor?.tf?.select?.(end);
            collapsed = true;
        }
    } catch {
        /* defensive — cell may have been removed under us */
    }
    clearCellSelection(editor);
    return collapsed;
}

// With a multi-CELL table selection active, `editor.api.fragment()` (the
// selection→fragment path `handleCopy` used) returns each selected cell's
// FULL row — unselected columns included, not just the highlighted rectangle
// — because the underlying Slate range spans from the first cell's start to
// the last cell's end, and the base (non-table-aware) fragment builder just
// slices document structure between those two points rather than reasoning
// about which cells are actually selected. Rows in that malformed fragment
// end up with mismatched cell counts, which the paste side (and a plain-text
// clipboard fallback) can't reconstruct into a grid — it collapses to a
// tab-separated dump into a single cell.
//
// Fix: when `getSelectedCells` reports a genuine multi-cell selection, build
// the rectangular sub-table directly from those cells (each cell's own
// [row, col] position, via `editor.api.findPath`) instead of asking the
// generic fragment API to infer the shape from a Range. Returns `null` for
// anything else (single-cell / non-table selection) so the caller falls back
// to the existing per-editor fragment path unchanged.
//
// The rebuilt table/tr nodes carry the SOURCE table's and rows' own props
// (colSizes — subset by the selected columns — marginLeft, row size, …). A
// bare {type:'table'}/{type:'tr'} rebuild pasted as an unformatted table with
// evenly-distributed columns and default row heights. `id` is excluded: the
// paste must not duplicate the live source node's id.
function withoutChildrenAndId(node: any): any {
    const { children: _children, id: _id, ...props } = (node ?? {}) as any;
    return props;
}

// The library's in-table getFragment() rebuilds the table node from scratch
// (api.create.table) — no colSizes, no table props. Graft the source table's
// props back on. colSizes only when the full source array still matches the
// fallback's span-aware column count (a whole-table copy) — a column subset
// can't reuse the full-width array.
function graftSourceTableProps(fragmentTable: any, sourceTable: any): any {
    if (!sourceTable || typeof sourceTable !== 'object') return fragmentTable;
    const { colSizes: sourceColSizes, type: _type, ...sourceProps } = withoutChildrenAndId(sourceTable);
    const grafted: any = { ...JSON.parse(JSON.stringify(sourceProps)), ...fragmentTable };
    if (
        !Array.isArray(fragmentTable?.colSizes) &&
        Array.isArray(sourceColSizes) &&
        sourceColSizes.length > 0 &&
        sourceColSizes.length === tableFragmentColumnCount(fragmentTable)
    ) {
        grafted.colSizes = [...sourceColSizes];
    }
    return grafted;
}

// Resolves the current multi-cell selection to its rectangular grid: the
// sorted distinct row/col indices, the grid of live cell nodes, and the
// source table node (cell paths are [...tablePath, row, col]). `ragged: true`
// marks a selection that can't form a rectangle — a merged (colSpan/rowSpan)
// cell is reported once at its own [row, col], so the slots it visually spans
// have no entry and rows disagree on width. Returns null for anything that
// isn't a genuine multi-cell selection.
function collectSelectedCellsGrid(
    editor: any,
): { rows: number[]; cols: number[]; grid: any[][]; sourceTable: any; ragged: boolean } | null {
    const cells = (getSelectedCells as any)(editor);
    if (!Array.isArray(cells) || cells.length < 2) return null;

    const positioned = cells
        .map((cell: any) => {
            const path: number[] | undefined = editor?.api?.findPath?.(cell);
            if (!path || path.length < 2) return null;
            return { cell, row: path[path.length - 2], col: path[path.length - 1] };
        })
        .filter((entry: any): entry is { cell: any; row: number; col: number } => entry !== null);
    if (positioned.length < 2) return null;

    const rows = [...new Set(positioned.map((p) => p.row))].sort((a, b) => a - b);
    const cols = [...new Set(positioned.map((p) => p.col))].sort((a, b) => a - b);

    const firstCellPath: number[] | undefined = editor?.api?.findPath?.(positioned[0].cell);
    const sourceTable: any = firstCellPath && firstCellPath.length >= 3 ? editor?.api?.node?.(firstCellPath.slice(0, -2))?.[0] : undefined;

    const grid = rows.map((row) => cols.map((col) => positioned.find((p) => p.row === row && p.col === col)?.cell).filter(Boolean));
    return { rows, cols, grid, sourceTable, ragged: grid.some((row) => row.length !== cols.length) };
}

export function buildSelectedCellsTableFragment(editor: any): any[] | null {
    try {
        const collected = collectSelectedCellsGrid(editor);
        if (!collected) return null;
        const { rows, cols, grid, sourceTable, ragged } = collected;

        // A ragged (merged-cell) selection can't be represented as this
        // function's simple rectangle. Before giving up entirely, try
        // `editor.getFragment()` — Slate's own selection-aware fragment
        // builder, which DOES understand spans — and use its result if it
        // resolves to a single table-typed fragment.
        if (ragged) {
            try {
                const fallback = (editor as any)?.getFragment?.();
                if (Array.isArray(fallback) && fallback.length === 1 && fallback[0]?.type === 'table') {
                    return [graftSourceTableProps(fallback[0], sourceTable)];
                }
            } catch {
                /* fall through to null below */
            }
            return null;
        }

        const table: any = {
            ...withoutChildrenAndId(sourceTable),
            type: 'table',
            children: grid.map((row, ri) => ({
                ...withoutChildrenAndId(sourceTable?.children?.[rows[ri]]),
                type: 'tr',
                children: row,
            })),
        };
        const sourceColSizes: unknown = sourceTable?.colSizes;
        if (Array.isArray(sourceColSizes) && sourceColSizes.length > 0) {
            // Subset by the selected columns so a partial-column copy keeps the
            // widths of exactly the columns it carries (missing entries → 0,
            // Plate's own auto-width marker).
            table.colSizes = cols.map((c) => (typeof sourceColSizes[c] === 'number' ? sourceColSizes[c] : 0));
        } else {
            delete table.colSizes;
        }
        // Deep-clone so the copied fragment doesn't share node references with
        // the live document (Plate/Slate nodes are plain JSON, same assumption
        // the paste-side fragment decode already relies on).
        return [JSON.parse(JSON.stringify(table))];
    } catch {
        return null;
    }
}

// Renders the selected-cells rectangle to formatting-rich clipboard HTML by
// cloning each cell's RENDERED DOM — inline marks (size / color / highlight /
// font), cell backgrounds, and real <ul>/<ol> list markup all ride along —
// then assembling a fresh <table> run through the same cleanDomForExport +
// enrichPastedListElements pass as the generic (non-table) copy path.
// DOM-based because a copy handler must write clipboardData synchronously;
// the plateToHtml serializer is async (image docId resolution) and can't run
// inside the event. Returns null when the selection isn't a clean rectangle
// or a cell has no rendered DOM — the caller falls back to the plain-text
// table so every clipboard format keeps describing the same cells.
export function buildSelectedCellsTableDomHtml(editor: any): string | null {
    try {
        const collected = collectSelectedCellsGrid(editor);
        if (!collected || collected.ragged) return null;
        const table = document.createElement('table');
        const tbody = document.createElement('tbody');
        table.appendChild(tbody);
        for (const gridRow of collected.grid) {
            const tr = document.createElement('tr');
            for (const cell of gridRow) {
                const cellDom = editor?.api?.toDOMNode?.(cell);
                if (!(cellDom instanceof HTMLElement)) return null;
                tr.appendChild(cellDom.cloneNode(true));
            }
            tbody.appendChild(tr);
        }
        const wrapper = document.createElement('div');
        wrapper.appendChild(table);
        cleanDomForExport(wrapper);
        enrichPastedListElements(wrapper, 1);
        return wrapper.innerHTML;
    } catch {
        return null;
    }
}

// Escape the characters that would otherwise be interpreted as markup when
// interpolated into a hand-built HTML string (cell text routinely contains
// `<1%`, `P&L`, etc. — unescaped, those corrupt the clipboard HTML's markup).
function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Renders a `buildSelectedCellsTableFragment` result to `text/html` (a real
// `<table>`) and `text/plain` (tab/newline-separated cells) — the two
// clipboard fallback channels external targets (Word, Sheets, a plain input)
// and same-editor plain-text paste read. Built directly from the rectangular
// fragment (not the DOM selection) so all three clipboard formats agree on
// exactly which cells are in the copy.
export function tableFragmentToClipboardText(tableFragment: any[]): { html: string; text: string } {
    const rows: any[] = tableFragment[0]?.children ?? [];
    const cellTexts = rows.map((row) => (row?.children ?? []).map((cell: any) => NodeApi.string(cell)));
    const html = `<table>${cellTexts
        .map((row) => `<tr>${row.map((text) => `<td>${escapeHtml(text)}</td>`).join('')}</tr>`)
        .join('')}</table>`;
    const text = cellTexts.map((row) => row.join('\t')).join('\n');
    return { html, text };
}

// Clears a cut/delete target's content. A genuine multi-cell rectangle
// (getSelectedCells().length > 1) has no single Slate Range spanning it, so
// each selected cell's content is reset directly rather than through
// editor.selection. Anything else (a normal text/single-cell selection) still
// has a real Slate Range, so the standard deleteFragment transform clears it.
export function clearCutSelection(editor: any): void {
    try {
        const selectedCells = (getSelectedCells as any)(editor);
        if (Array.isArray(selectedCells) && selectedCells.length > 1) {
            editor?.tf?.withoutNormalizing?.(() => {
                selectedCells.forEach((cell: any) => {
                    const path: number[] | undefined = editor?.api?.findPath?.(cell);
                    if (!path) return;
                    // setNodes can't touch `children`, so replace the cell's
                    // content in place: drop its existing block children and
                    // insert one empty paragraph (getEmptyCellNode's own child,
                    // so the paragraph type matches the editor's config). Cell
                    // attributes (background, colSpan) are preserved.
                    const childCount = cell?.children?.length ?? 0;
                    for (let i = childCount - 1; i >= 0; i--) {
                        editor?.tf?.removeNodes?.({ at: [...path, i] });
                    }
                    const emptyParagraph = getEmptyCellNode(editor, {})?.children?.[0];
                    if (emptyParagraph) editor?.tf?.insertNodes?.(emptyParagraph, { at: [...path, 0] });
                });
            });
            return;
        }
        editor?.tf?.deleteFragment?.();
    } catch {
        /* defensive — selection may have moved under us */
    }
}

export interface UseClipboardHandlersConfig {
    editor: any;
    readOnly: boolean;
    /**
     * `uploadSource` tag passed through to `handleImageInsert` so the upload
     * pipeline knows which surface initiated the upload (one-pager, email,
     * comment, etc.) for analytics + scoping.
     */
    uploadSource: string;
    /**
     * Strip foreign text styling (colour / font size / family / background) and
     * flatten layout tables on paste, keeping links + lists + basic marks. Used
     * by compact inputs like the comments box so pasted rich HTML (e.g. a Fathom
     * email) lands as clean text rather than washed-out, oversized, table-wrapped
     * content (RECUR-26761). Default off — document editors keep full fidelity.
     */
    simplifyPaste?: boolean;
}

export interface UseClipboardHandlersReturn {
    /**
     * The clipboard handlers return `true` when they handled the event. This
     * is how Plate's handler pipe (and slate-react behind it) is told to skip
     * the built-in behavior — preventDefault alone is IGNORED by that chain.
     */
    handlePaste: (e: React.ClipboardEvent) => boolean | void;
    handleDrop: (e: React.DragEvent) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleCopy: (e: React.ClipboardEvent) => boolean | void;
    handleCut: (e: React.ClipboardEvent) => boolean | void;
    /**
     * Call this from the editor's keydown handler when ⌘⇧V / Ctrl⇧V is
     * detected. The paste event fires immediately after, and the next
     * `handlePaste` invocation reads this flag and switches to plain-text
     * mode. The flag is one-shot — consumed by the next paste regardless
     * of mode.
     */
    markNextPasteAsPlain: () => void;
}

export function useClipboardHandlers({
    editor,
    readOnly,
    uploadSource,
    simplifyPaste = false,
}: UseClipboardHandlersConfig): UseClipboardHandlersReturn {
    // ⌘⇧V / Ctrl⇧V intent. Set on keydown, consumed by handlePaste, cleared
    // either way. A ref (not state) because the keydown→paste sequence is
    // synchronous and React state would update too late to be read by the
    // paste handler in the same tick.
    const plainTextPasteRef = useRef(false);

    const markNextPasteAsPlain = useCallback(() => {
        plainTextPasteRef.current = true;
    }, []);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            if (readOnly) return;

            const ed: any = editor;

            // A single click puts the cell in Excel-style "selected" mode (the
            // WHOLE cell is range-selected). Left as-is, every insert path below
            // would replace the cell's content on paste. Collapse to the cell's
            // end first so paste APPENDS instead of overwriting. Runs before all
            // branches (plain-text, x-slate-fragment, HTML) so they all append.
            // Best-effort — a false return means the selected-cell node was
            // stale (unresolvable), in which case the live Slate selection is
            // already somewhere else and the paste should proceed against it.
            collapseSelectedCellForPaste(ed);

            // ⌘⇧V intent: insert the plain-text payload, ignoring HTML AND
            // the `application/x-slate-fragment` back-channel (which Plate's
            // own paste handler would otherwise decode and reinstate every
            // mark). Splits on newlines so multi-line plain text becomes
            // multiple paragraphs, matching Google Docs / Notion behavior.
            // NOTE on the `return true`s below: Plate's handler pipe treats an
            // event as handled ONLY on stopPropagation or a `true` return — its
            // isEventHandled never consults isDefaultPrevented — and slate-react
            // then trusts that boolean over its own defaultPrevented check. A
            // handled branch that doesn't return true lets slate-react re-run
            // its default insertData/setFragmentData over what we just did.
            if (plainTextPasteRef.current) {
                plainTextPasteRef.current = false;
                e.preventDefault();
                e.stopPropagation();
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (!text) return true;
                const lines = text.split(/\r?\n/);
                lines.forEach((line, i) => {
                    if (i > 0) ed?.tf?.insertBreak?.();
                    if (line) ed?.tf?.insertText?.(line);
                });
                scrollCaretIntoView(ed);
                return true;
            }

            // ── Slate/Plate native fragment (same or another Plate surface) ──
            // This is the only path that preserves list marks (indent +
            // listStyleType) perfectly on Plate-to-Plate copy-paste. Checked
            // BEFORE the HTML path so the rich AST wins over the lossy HTML.
            const rawFragment = e.clipboardData?.getData('application/x-slate-fragment');
            if (rawFragment) {
                const fragment = decodeSlateFragment(rawFragment);
                if (fragment) {
                    e.preventDefault();
                    e.stopPropagation();
                    insertPastedFragment(ed, sanitizePlateFragment(stripNewlinesFromFragment(fragment)));
                    scrollCaretIntoView(ed);
                    return true;
                }
                /* malformed — fall through to HTML path */
            }

            // Read the HTML up front. Excel (and Word) put a BITMAP of the
            // copied selection on the clipboard ALONGSIDE the real table HTML.
            // If the image branch below runs first it grabs that bitmap and the
            // Excel table pastes as an image — Google Sheets attaches no bitmap,
            // which is why it always worked. When the clipboard carries
            // table-shaped HTML (spreadsheet OR plain document table), prefer it
            // over the image. `htmlIsTable` gates ONLY the bitmap guard — the
            // converter-vs-generic routing below keys off `isSpreadsheetTableHtml`
            // alone.
            const html = e.clipboardData?.getData('text/html') ?? '';
            const htmlIsSpreadsheet = !!html && isSpreadsheetTableHtml(html);
            const htmlIsTable = htmlIsSpreadsheet || (!!html && isPureTableHtml(html));

            // Image file in the clipboard → image paste, UNLESS we have table
            // HTML to use instead (the Excel bitmap-vs-table case above). A
            // genuine image paste (screenshot, copied <img>) carries no
            // table-shaped HTML, so it still routes here.
            if (!htmlIsTable) {
                const items = e.clipboardData?.items ? Array.from(e.clipboardData.items) : [];
                for (const item of items) {
                    if (item.kind === 'file' && item.type.startsWith('image/')) {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = item.getAsFile();
                        if (!file) return true;
                        (async () => {
                            const result = await handleImageInsert(file, editor, uploadSource);
                            if (result.error === 'invalid-mime') toast.error('Invalid image file');
                            else if (result.error === 'too-large') toast.error('Image too large (max 8 MB)');
                            else scrollCaretIntoView(ed);
                        })();
                        return true;
                    }
                }
            }

            if (!html) return;
            e.preventDefault();
            e.stopPropagation();
            // Route to the spreadsheet converter ONLY for genuine Excel / Google
            // Sheets HTML (the `isSpreadsheetTableHtml` signatures). Detection runs
            // here, BEFORE sanitizeOfficeHtml strips the <style> blocks Excel uses
            // as its marker. `spreadsheetHtmlToPlateTable`'s cellBlocks() maps only
            // <p>/<div> → blocks and ignores <ol>/<ul>, so a DOCUMENT table (Google
            // Docs, generic HTML — isPureTableHtml true but isSpreadsheetTableHtml
            // false) must fall through to the generic deserializer, which preserves
            // cell lists. Rich documents that merely contain a table likewise fall
            // through, so the converter never collapses a multi-block paste to its
            // first table.
            if (htmlIsSpreadsheet) {
                try {
                    const tableFragment = spreadsheetHtmlToPlateTable(html);
                    if (tableFragment) {
                        insertPastedFragment(ed, sanitizePlateFragment(stripNewlinesFromFragment([tableFragment])));
                        scrollCaretIntoView(ed);
                        return true;
                    }
                } catch {
                    /* fall through */
                }
            }
            try {
                const deserialized = deserializePastedHtml(editor, html);
                // Compact inputs (comments) strip foreign styling + flatten layout
                // tables so pasted rich HTML lands as clean text (RECUR-26761).
                const fragment = deserialized && simplifyPaste ? simplifyPastedFragment(deserialized) : deserialized;
                if (fragment && fragment.length) {
                    insertPastedFragment(ed, sanitizePlateFragment(fragment));
                    // Pasted <img> off the web (e.g. Google Docs googleusercontent
                    // URLs) lands as an image node with a remote http url that the
                    // save pipeline ignores — it would render until the source URL
                    // expires, then break. Re-host each such image into our S3 in
                    // the background (graceful no-op + warn for CORS-blocked hosts).
                    rehostRemoteImagesAfterPaste(ed, uploadSource);
                    scrollCaretIntoView(ed);
                    return true;
                }
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) {
                    ed?.tf?.insertText?.(text);
                    scrollCaretIntoView(ed);
                }
            } catch {
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (text) {
                    ed?.tf?.insertText?.(text);
                    scrollCaretIntoView(ed);
                }
            }
            return true;
        },
        // `uploadSource` belongs in deps — the original implementation in
        // PlateEditor.tsx omitted it, which would have caused stale closures
        // if the prop ever changed mid-life. Fixed during extraction.
        [editor, readOnly, uploadSource, simplifyPaste],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            if (readOnly) return;
            const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
            if (files.length === 0) return;
            // Prevent Slate's "Cannot resolve a Slate range" error on file drops.
            e.preventDefault();
            e.stopPropagation();
            const imageFiles = files.filter((f) => f.type.startsWith('image/'));
            for (const file of imageFiles) {
                (async () => {
                    const result = await handleImageInsert(file, editor, uploadSource);
                    if (result.error === 'invalid-mime') toast.error(`Invalid image file: ${file.name}`);
                    else if (result.error === 'too-large') toast.error(`Image too large (max 8 MB): ${file.name}`);
                })();
            }
        },
        // Same fix — uploadSource added to deps.
        [editor, readOnly, uploadSource],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        const types = e.dataTransfer?.types;
        if (types && Array.from(types).includes('Files')) e.preventDefault();
    }, []);

    // Shared by handleCopy and handleCut: writes the current selection to the
    // clipboard event's DataTransfer across all three formats (Plate AST,
    // HTML, plain text). Returns `false` (nothing written, caller should not
    // preventDefault) when the selection isn't inside this editor.
    const writeSelectionToClipboard = useCallback(
        (e: React.ClipboardEvent): boolean => {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) return false;
            const range = sel.getRangeAt(0);
            const container =
                range.commonAncestorContainer instanceof HTMLElement
                    ? range.commonAncestorContainer
                    : range.commonAncestorContainer.parentElement;
            if (!container?.closest?.('[data-testid="plate-editor-content"]')) return false;
            const frag = range.cloneContents();
            const wrapper = document.createElement('div');
            wrapper.appendChild(frag);
            cleanDomForExport(wrapper);

            // A multi-CELL table selection needs its own rectangular fragment —
            // the generic per-editor fragment API below returns each selected
            // cell's full row (unselected columns included), which produces
            // mismatched-width rows the paste side can't reconstruct into a
            // grid. When this applies, it drives ALL THREE clipboard formats
            // (AST, HTML, plain-text) so they can't disagree with each other;
            // otherwise fall through to the existing per-editor fragment path.
            const selectedCellsFragment = buildSelectedCellsTableFragment(editor);

            // ── Plate AST fragment ──────────────────────────────────────────
            // Write the exact Plate/Slate AST to `application/x-slate-fragment`
            // so that pasting back into a Plate editor restores list marks
            // (indent + listStyleType) perfectly, without relying on the lossy
            // HTML round-trip. The paste handler checks for this format first.
            try {
                const rawPlateFragment = selectedCellsFragment ?? (editor as any)?.api?.fragment?.() ?? (editor as any)?.getFragment?.();
                // api.fragment() wraps ANY in-cell selection as a 1×1 table
                // (ancestor chain kept), which the paste side would spread over
                // the destination cell — replacing its content. Unwrap 1×1
                // tables to their cell content so an in-cell copy pastes as
                // content. selectedCellsFragment is always ≥2 cells, so real
                // rectangle copies are untouched. Accepted ambiguity: a whole
                // deliberately-authored one-cell table is indistinguishable
                // from an in-cell selection here, so it can never be copied AS
                // a table — acceptable because no app flow authors 1×1 tables
                // (boxed callouts use CalloutPlugin, templates/markdown emit
                // multi-cell tables only).
                const plateFragment = Array.isArray(rawPlateFragment) ? unwrapSingleCellTables(rawPlateFragment) : rawPlateFragment;
                if (Array.isArray(plateFragment) && plateFragment.length) {
                    // getFragment drops table-level colSizes — re-attach from the
                    // source table so paste preserves column widths.
                    reattachTableColSizes(editor, plateFragment);
                    e.clipboardData.setData('application/x-slate-fragment', encodeSlateFragment(plateFragment));
                }
            } catch {
                /* defensive — fall back to HTML-only clipboard */
            }

            if (selectedCellsFragment) {
                const { html, text } = tableFragmentToClipboardText(selectedCellsFragment);
                // text/html carries the formatting-rich DOM render (cell
                // backgrounds, marks, lists) so external targets — Word, Docs,
                // Sheets — paste with source formatting. The plain rebuilt
                // table is the fallback when the cells aren't rendered.
                const richHtml = buildSelectedCellsTableDomHtml(editor);
                e.clipboardData.setData('text/html', `<html><body>${richHtml ?? html}</body></html>`);
                e.clipboardData.setData('text/plain', text);
                return true;
            }

            // ── HTML fallback (external targets: Word, Docs, plain inputs) ──
            // Enrich <li> elements with data-indent + data-list-style-type so
            // any Plate editor that receives this HTML can reconstruct list
            // marks even without the x-slate-fragment format.
            enrichPastedListElements(wrapper, 1);

            e.clipboardData.setData('text/html', `<html><body>${wrapper.innerHTML}</body></html>`);
            e.clipboardData.setData('text/plain', wrapper.textContent ?? '');
            return true;
        },
        [editor],
    );

    const handleCopy = useCallback(
        (e: React.ClipboardEvent) => {
            if (!writeSelectionToClipboard(e)) return;
            // stopPropagation + `return true` are LOAD-BEARING, not defensive:
            // Plate's handler pipe ignores preventDefault (see the paste-handler
            // note) — without them slate-react re-runs setFragmentData and
            // @platejs/table's withSetFragmentDataTable CLOBBERS everything
            // written above with its own base64 fragment + bare-text HTML.
            e.preventDefault();
            e.stopPropagation();
            return true;
        },
        [writeSelectionToClipboard],
    );

    // Cut = copy's clipboard write + clearing the selection's content
    // (clearCutSelection, above). slate-react ships a default onCut
    // (ReactEditor.setFragmentData + Editor.deleteFragment), but its
    // clipboard write goes through the same non-table-aware base fragment
    // builder handleCopy exists to bypass — a multi-cell table selection
    // would cut a malformed (mismatched-row) fragment. Own the handler so
    // cut gets the same table-aware clipboard write as copy, before clearing.
    const handleCut = useCallback(
        (e: React.ClipboardEvent) => {
            if (readOnly) return;
            if (!writeSelectionToClipboard(e)) return;
            // Same load-bearing stopPropagation + `true` as handleCopy — and for
            // cut, slate-react's default would ALSO deleteFragment a second time.
            e.preventDefault();
            e.stopPropagation();
            clearCutSelection(editor);
            return true;
        },
        [editor, readOnly, writeSelectionToClipboard],
    );

    return { handlePaste, handleDrop, handleDragOver, handleCopy, handleCut, markNextPasteAsPlain };
}
